import { StateParams, StateProvider, StateService } from "@uirouter/angularjs";
import { vsComponentName as processGridComponentName } from "./views/ProcessInstancesGrid";
import {
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
  RecordGridViewService,
  vsServiceName as RecordGridViewServiceName,
} from "../records/views/recordGridView";
import { processInstancesName } from "./config/objects";
import {
  ProcessInformationService,
  vsServiceName as ProcessInformationServiceName,
} from "./services/processInformationService";
import { ProcessGroup, ProcessInformation } from "./model/ProcessGroups";
import { IScope } from "angular";
import {
  GridSettingsService,
  IGridSettings,
  vsName as GridSettingsServiceName,
} from "../records/grids/settings/gridSettingsService";
import { ColDef } from "../records/grids/columns/colDef";

import { vsComponentName as ProcessTemplateEditComponentName } from "./views/EmailTemplate/edit";
import { vsComponentName as ProcessNotificationComponentName } from "./views/Notification";
import { vsComponentName as ProcessNotificationEditComponentName } from "./views/Notification/edit";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../records/server/serverUtility";
import { ServiceResponse } from "../records/server/query";

import {
  vsObjectName as slotcheckEmailTemplateName,
  vsGridSettingsSymbol as slotcheckEmailTemplateSymbol,
} from "./config/objects/slotcheckEmailTemplate";
import {
  vsName as ColumnDefinitionServiceName,
  ColumnDefinitionService,
} from "../records/grids/columns/columnDefinitionService";
import { getProcessDetailState } from "./config";

routeConfig.$inject = ["$stateProvider"];

const baseRoute = "main.processes";

export function routeConfig($stateProvider: StateProvider) {
  $stateProvider
    .state(baseRoute, {
      abstract: true,
      template: "<ui-view></ui-view>",
      url: "processes",
    })
    .state(`${baseRoute}.grid`, {
      url: "",
      component: processGridComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService) =>
            recordGridViewService.getGridConfiguration(processInstancesName),
        ],
        processGroups: [
          ProcessInformationServiceName,
          (processInformationService: ProcessInformationService) =>
            processInformationService.getAuthorizedGetProcessGroups(),
        ],
      },
    })
    .state(`${baseRoute}.grid.group`, {
      url: "/{groupId:[^/]+}",
      template:
        require("!html-loader!./views/ProcessInstancesGrid/groupTemplate.html")
          .default,
      controller: [
        "$scope",
        "processGroup",
        "gridSettings",
        "additionalGridColumns",
        ProcessInformationServiceName,
        (
          s: IScope,
          processGroup: ProcessGroup,
          gridSettings: IGridSettings,
          additionalGridColumns: ColDef[],
          processInformationService: ProcessInformationService
        ) => {
          const $scope = <any>s;
          $scope.processGroup = processGroup;
          $scope.gridSettings = gridSettings;
          $scope.onNewProcessSelected = (process: ProcessInformation) =>
            processInformationService
              .createNewProcessFlow(processGroup, process)
              .then((processResponse) =>
                processInformationService.navigateToDetail(processResponse)
              );
          $scope.defaultQuery = undefined;
          $scope.additionalGridColumns = additionalGridColumns;
        },
      ],
      resolve: {
        groupId: [
          "$stateParams",
          ($stateParams: StateParams) => $stateParams.groupId,
        ],
        processGroup: [
          "$stateParams",
          ProcessInformationServiceName,
          (
            $stateParams: StateParams,
            processInformationService: ProcessInformationService
          ) => processInformationService.getProcessGroup($stateParams.groupId),
        ],
        gridSettings: [
          ProcessInformationServiceName,
          "processGroup",
          (
            processInformationService: ProcessInformationService,
            processGroup: ProcessGroup
          ) => processInformationService.getGridSettings(processGroup),
        ],
        additionalGridColumns: [
          "processGroup",
          ProcessInformationServiceName,
          ColumnDefinitionServiceName,
          (
            processGroup: ProcessGroup,
            processInformationService: ProcessInformationService,
            columnDefinitionService: ColumnDefinitionService
          ) => [
            columnDefinitionService.getLinkColDef({
              headerName: "info",
              iconClass: "fa fa-info-circle text-primary",
              colId: "detail",
              width: 20,
              stateExpression: `${processInformationService.getDetailState(
                processGroup.Id
              )}({  processInstancesId: data.ProcessInstancesId})`,
            }),
            ...processInformationService.getAdditionalGridColumns(processGroup),
          ],
        ],
      },
    })
    .state(
      `${baseRoute}.grid.group.detail`,
      getProcessDetailState([
        "processGroup",
        ProcessInformationServiceName,
        (
          processGroup: ProcessGroup,
          processInformationService: ProcessInformationService
        ) => processInformationService.getDetailTemplate(processGroup.Id),
      ])
    )
    .state(`${baseRoute}.template`, {
      url: "/template",
      abstract: true,
      template: "<ui-view></ui-view>",
      resolve: {
        placeholderOptions: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility) {
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: `api/process/template/placeholder`,
              })
              .then((r) => (<ServiceResponse<unknown>>r).Results);
          },
        ],
      },
    })
    .state(`${baseRoute}.template.grid`, {
      url: "",
      component: RecordGridViewComponentName,
      resolve: {
        gridConfiguration: [
          RecordGridViewServiceName,
          (recordGridViewService: RecordGridViewService) =>
            recordGridViewService.getGridConfiguration(
              slotcheckEmailTemplateName
            ),
        ],
        gridDataSource: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.gridDataSource,
        ],
        gridSettings: [
          GridSettingsServiceName,
          (gridSettingsService: GridSettingsService) =>
            gridSettingsService.getGridSettings(slotcheckEmailTemplateSymbol),
        ],
        columns: [
          "gridConfiguration",
          ColumnDefinitionServiceName,
          (
            gridConfiguration: GridConfiguration,
            columnDefinitionService: ColumnDefinitionService
          ) => [
            columnDefinitionService.getLinkColDef({
              colId: "edit",
              headerName: "edit",
              iconClass: "fa fa-pencil text-primary",
              stateExpression: "^.edit({templateId: data.Id})",
              requiresPermission: "processEmailTemplateModify",
            }),
            ...(gridConfiguration.columns ?? []),
          ],
        ],
        defaultSort: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.defaultSort,
        ],
        defaultQuery: [() => undefined],
        nameSingular: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.nameSingular,
        ],
        namePlural: [
          "gridConfiguration",
          (gridConfiguration: GridConfiguration) =>
            gridConfiguration.namePlural,
        ],
        showAddButton: [() => true],
        showAddButtonPermission: [() => "processEmailTemplateModify"],
        onAddNew: [
          "$state",
          ($state: StateService) => [() => $state.go("^.add")],
        ],
      },
    })
    .state(`${baseRoute}.template.add`, {
      url: "/add",
      component: ProcessTemplateEditComponentName,
      resolve: {
        model: [
          () => {
            return {};
          },
        ],
      },
    })
    .state(`${baseRoute}.template.edit`, {
      url: "/edit/{templateId:int}",
      component: ProcessTemplateEditComponentName,
      resolve: {
        model: [
          ServerUtilityName,
          "$stateParams",
          function (serverUtility: ServerUtility, $stateParams: StateParams) {
            const templateId = $stateParams["templateId"];
            return serverUtility
              .sendApiRequest({
                method: "get",
                url: `api/process/template/${templateId}`,
              })
              .then((r) => (<ServiceResponse<unknown>>r).Result);
          },
        ],
      },
    })
    .state(`${baseRoute}.notification`, {
      url: "/notification",
      component: ProcessNotificationComponentName,
      resolve: {
        processGroups: [
          ProcessInformationServiceName,
          (processInformationService: ProcessInformationService) =>
            processInformationService.getProcessGroups(),
        ],
      },
    })
    .state(`${baseRoute}.notification.add`, {
      url: "/add",
      component: ProcessNotificationEditComponentName,
      resolve: {
        model: [
          () => {
            return {};
          },
        ],
      },
    })
    .state(`${baseRoute}.notification.edit`, {
      url: "/{actionId:int}/edit",
      component: ProcessNotificationEditComponentName,
      resolve: {
        model: [
          "$stateParams",
          ServerUtilityName,
          ($stateParams: StateParams, serverUtility: ServerUtility) => {
            return serverUtility
              .getById<ServiceResponse<any>>({
                url: "/api/process/notification",
                id: $stateParams.actionId,
              })
              .then((r) => r.Result);
          },
        ],
      },
    });
}
